import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Email,
  VerifiedUser,
  Refresh,
} from '@mui/icons-material';
import { authApi } from '../services/api';

const ConfirmEmailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 新規登録ページから渡されたemailを取得、なければ空文字
  const initialEmail = location.state?.email || '';
  
  const [email, setEmail] = useState(initialEmail);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [resendMessage, setResendMessage] = useState<string>('');

  const handleConfirm = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!email.trim()) {
      setError('メールアドレスを入力してください');
      return;
    }
    
    if (!confirmationCode.trim()) {
      setError('確認コードを入力してください');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authApi.confirmEmail(email, confirmationCode);
      navigate('/login', { 
        state: { 
          message: 'メールアドレスが確認されました。ログインしてください。' 
        } 
      });
      
    } catch (error: any) {
      console.error('Confirmation error:', error);
      setError(
        error.response?.data?.error || 
        'メール確認中にエラーが発生しました'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email.trim()) {
      setError('メールアドレスを入力してください');
      return;
    }

    setResendLoading(true);
    setError('');
    setResendMessage('');

    try {
      await authApi.resendConfirmationCode(email);
      setResendMessage('確認コードを再送信しました。メールをご確認ください。');
    } catch (error: any) {
      console.error('Resend error:', error);
      setError(
        error.response?.data?.error || 
        '確認コード再送信中にエラーが発生しました'
      );
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <VerifiedUser sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography component="h1" variant="h4" gutterBottom>
              メール確認
            </Typography>
            <Typography variant="body1" color="text.secondary">
              確認コードをメールアドレスに送信しました。
              <br />
              メールに記載された6桁の確認コードを入力してください。
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleConfirm} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {resendMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {resendMessage}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="メールアドレス"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email />
                  </InputAdornment>
                ),
              }}
              disabled={!!initialEmail} // 新規登録から来た場合は編集不可
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="confirmationCode"
              label="確認コード（6桁）"
              name="confirmationCode"
              value={confirmationCode}
              onChange={(e) => setConfirmationCode(e.target.value)}
              inputProps={{
                maxLength: 6,
                pattern: '[0-9]{6}',
              }}
              placeholder="123456"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'メールアドレスを確認'
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                確認コードが届かない場合
              </Typography>
              <Button
                variant="text"
                onClick={handleResendCode}
                disabled={resendLoading}
                startIcon={resendLoading ? <CircularProgress size={16} /> : <Refresh />}
              >
                確認コードを再送信
              </Button>
            </Box>

            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="text"
                onClick={() => navigate('/login')}
              >
                ログインページに戻る
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ConfirmEmailPage;
